import { differenceInCalendarDays, format as dateFormat, isValid, Locale, parseISO } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { TFunction } from 'i18next'

const LOCALE_MAP: Record<string, Locale> = {
  'en-US': enUS,
  'es-US': es,
}

/**
 * Resolves a date-fns Locale from `window.locale`. Falls back to enUS when
 * `window` is not available (e.g. SSR / tests) or the locale is unsupported.
 */
export function getDateFnsLocale(): Locale {
  if (typeof window === 'undefined' || !('locale' in window)) {
    return enUS
  }
  const windowLocale = window.locale as string
  return LOCALE_MAP[windowLocale] || enUS
}

interface ParsedDate {
  formattedDate: string
  isoDate: string
}

interface DateUtil {
  parseISODate: (input: string, format?: string) => ParsedDate
  format: (d: Date, formatStr: string) => string
  formatRelativePriorDate: (t: TFunction, date: Date, options?: { from?: Date; withTime?: boolean }) => string
}

export const date = (locale: Locale = getDateFnsLocale()): DateUtil => ({
  parseISODate(input, format) {
    const date = parseISO(input)
    return {
      formattedDate: isValid(date)
        ? dateFormat(date, format ?? 'MMM dd', {
            locale,
          })
        : '',
      isoDate: input,
    }
  },

  // Wrapper around date-fns format that automatically uses the resolved locale
  format(d: Date, formatStr: string): string {
    return dateFormat(d, formatStr, { locale })
  },

  formatRelativePriorDate(t, date, { from = new Date(), withTime = false } = {}) {
    const numberDaysAgo = differenceInCalendarDays(from, date)

    let dateString: string
    if (numberDaysAgo === 0) {
      dateString = t('Today', { context: 'label for the current day' })
    } else if (numberDaysAgo === 1) {
      dateString = t('Yesterday', { context: 'label for the prior day' })
    } else if (numberDaysAgo < 7 && !withTime) {
      dateString = dateFormat(date, 'EEEE', { locale })
    } else {
      dateString = dateFormat(date, 'MMM d', { locale })
    }

    return withTime ? `${dateString} at ${dateFormat(date, 'h:mm a', { locale })}` : dateString
  },
})
